import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
    Search, ArrowLeft, X, RefreshCw, ExternalLink, Sparkles, Package,
    BarChart3, ShoppingCart, Tag, Ticket, CreditCard, MapPin, Store, Clock, Plus,
    HelpCircle,
} from 'lucide-react';

const TAB_ICONS = {
    dashboard: BarChart3,
    analytics: BarChart3,
    'store-details': Store,
    products: Package,
    orders: ShoppingCart,
    categories: Tag,
    coupons: Ticket,
    delivery: MapPin,
    timeslots: Clock,
    settings: CreditCard,
};

function normalize(s) {
    return (s || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Tokenised match: every search token must appear (substring) somewhere
 * in the item's haystack (label + sublabel + keywords). Score = sum of
 * earliest match positions, lower is better. Cheap, deterministic, RTL-safe.
 */
function score(item, query) {
    if (!query) return 0;
    const tokens = normalize(query).split(' ').filter(Boolean);
    const hay = normalize([item.label, item.sublabel, ...(item.keywords || [])].join(' '));
    let total = 0;
    for (const t of tokens) {
        const idx = hay.indexOf(t);
        if (idx < 0) return -1;
        total += idx;
    }
    return total;
}

/**
 * Build the full action list. Order within section preserved. Sections
 * appear as visual groups; flat-indexed for keyboard navigation.
 */
function buildItems({ tabs, products, onGoToTab, onRefresh, onOpenStorePublic, onClose, onShowShortcuts }) {
    const items = [];

    // Section: navigation
    (tabs || []).forEach((t) => {
        const Icon = TAB_ICONS[t.id] || Package;
        items.push({
            id: `nav:${t.id}`,
            section: 'ניווט',
            label: t.label,
            sublabel: `מעבר ל${t.label}`,
            keywords: [t.id, 'tab', 'נווט', 'go'],
            icon: Icon,
            tone: 'brand',
            run: () => { onGoToTab?.(t.id); onClose?.(); },
        });
    });

    // Section: actions
    items.push({
        id: 'act:refresh',
        section: 'פעולות',
        label: 'רענון',
        sublabel: 'טען מחדש את הנתונים בלשונית הנוכחית',
        keywords: ['refresh', 'reload', 'alt+r'],
        icon: RefreshCw,
        tone: 'info',
        run: () => { onRefresh?.(); onClose?.(); },
    });
    items.push({
        id: 'act:open-public',
        section: 'פעולות',
        label: 'צפייה בחנות הציבורית',
        sublabel: 'פתח את החנות בכרטיסייה חדשה',
        keywords: ['public', 'view', 'site', 'preview'],
        icon: ExternalLink,
        tone: 'info',
        run: () => { onOpenStorePublic?.(); onClose?.(); },
    });
    items.push({
        id: 'act:create-product',
        section: 'פעולות',
        label: 'מוצר חדש',
        sublabel: 'מעבר ללשונית המוצרים להוספה',
        keywords: ['create', 'new', 'product', 'add', '+'],
        icon: Plus,
        tone: 'brand',
        run: () => { onGoToTab?.('products'); onClose?.(); },
    });
    items.push({
        id: 'act:create-category',
        section: 'פעולות',
        label: 'קטגוריה חדשה',
        sublabel: 'מעבר ללשונית הקטגוריות',
        keywords: ['create', 'new', 'category', 'add'],
        icon: Plus,
        tone: 'brand',
        run: () => { onGoToTab?.('categories'); onClose?.(); },
    });
    items.push({
        id: 'act:create-coupon',
        section: 'פעולות',
        label: 'קופון חדש',
        sublabel: 'מעבר ללשונית הקופונים',
        keywords: ['create', 'new', 'coupon', 'discount'],
        icon: Plus,
        tone: 'brand',
        run: () => { onGoToTab?.('coupons'); onClose?.(); },
    });
    items.push({
        id: 'act:shortcuts',
        section: 'פעולות',
        label: 'קיצורי מקלדת',
        sublabel: 'הצג את כל הקיצורים הזמינים',
        keywords: ['shortcuts', 'keys', 'help', '?'],
        icon: HelpCircle,
        tone: 'info',
        run: () => { onShowShortcuts?.(); onClose?.(); },
    });

    // Section: products (only when matching is meaningful)
    (products || []).slice(0, 50).forEach((p) => {
        items.push({
            id: `prod:${p.product_id || p.id || p.name}`,
            section: 'מוצרים',
            label: p.name || 'מוצר',
            sublabel: p.category_name
                ? `${p.category_name} · ₪${Number(p.price ?? 0).toLocaleString('he-IL')}`
                : `₪${Number(p.price ?? 0).toLocaleString('he-IL')}`,
            keywords: ['product', p.category_name, p.sku].filter(Boolean),
            icon: Package,
            tone: 'brand',
            run: () => { onGoToTab?.('products'); onClose?.(); },
        });
    });

    return items;
}

const TONES = {
    brand: 'bg-brand-100 text-brand-700',
    info:  'bg-info-100 text-info-700',
};

function Item({ item, active, onHover, onSelect }) {
    const Icon = item.icon || Sparkles;
    const toneClass = TONES[item.tone] || TONES.brand;
    return (
        <button
            type="button"
            ref={(el) => { if (active && el) el.scrollIntoView({ block: 'nearest' }); }}
            onMouseEnter={onHover}
            onClick={onSelect}
            className={clsx(
                'group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-start transition-colors',
                active ? 'bg-brand-50' : 'hover:bg-surface-muted',
            )}
        >
            <span className={clsx('inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0', toneClass)}>
                <Icon className="w-[18px] h-[18px]" />
            </span>
            <div className="flex-1 min-w-0">
                <div className={clsx('font-semibold text-sm truncate', active ? 'text-ink-strong' : 'text-ink')}>
                    {item.label}
                </div>
                {item.sublabel && (
                    <div className="text-xs text-ink-subtle truncate">{item.sublabel}</div>
                )}
            </div>
            <ArrowLeft className={clsx(
                'w-4 h-4 shrink-0 transition-[color,transform] duration-150',
                active ? 'text-brand-600 -translate-x-0.5' : 'text-ink-faint',
            )} />
        </button>
    );
}

/**
 * Command palette. Opens on Ctrl/Cmd+K from anywhere in the admin.
 * Search across tabs, actions, and product names. Keyboard-first:
 *   ↑/↓ — navigate
 *   Enter — run
 *   Esc — close
 */
export function CommandPalette({
    open, onClose,
    tabs,
    products,
    onGoToTab,
    onRefresh,
    onOpenStorePublic,
    onShowShortcuts,
}) {
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);

    // Build items once per open + when data changes
    const items = useMemo(
        () => buildItems({ tabs, products, onGoToTab, onRefresh, onOpenStorePublic, onClose, onShowShortcuts }),
        [tabs, products, onGoToTab, onRefresh, onOpenStorePublic, onClose, onShowShortcuts],
    );

    const filtered = useMemo(() => {
        if (!query.trim()) return items.filter((i) => i.section !== 'מוצרים').slice(0, 24);
        const scored = items
            .map((i) => ({ i, s: score(i, query) }))
            .filter((x) => x.s >= 0)
            .sort((a, b) => a.s - b.s)
            .slice(0, 30)
            .map((x) => x.i);
        return scored;
    }, [items, query]);

    // Group filtered into sections for display
    const grouped = useMemo(() => {
        const out = new Map();
        for (const it of filtered) {
            const key = it.section || 'אחר';
            if (!out.has(key)) out.set(key, []);
            out.get(key).push(it);
        }
        return Array.from(out.entries());
    }, [filtered]);

    // Reset state on open
    useEffect(() => {
        if (open) {
            setQuery('');
            setActive(0);
            const t = setTimeout(() => inputRef.current?.focus(), 30);
            return () => clearTimeout(t);
        }
    }, [open]);

    // Clamp active index when filter shrinks
    useEffect(() => {
        if (active >= filtered.length) setActive(Math.max(0, filtered.length - 1));
    }, [filtered.length, active]);

    // Lock body scroll while open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open) return null;

    const onKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(filtered.length - 1, i + 1));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            const it = filtered[active];
            if (it) it.run();
        }
    };

    // Build flat index for active highlight inside grouped render
    let runningIndex = -1;

    return (
        <div
            className="fixed inset-0 z-modal flex items-start justify-center px-4 pt-[12vh] bg-ink/45 backdrop-blur-[3px] animate-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="פתיחה מהירה"
        >
            <div
                className="bg-surface rounded-2xl w-full max-w-xl shadow-elevated border border-line-subtle animate-scale-in overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
                onKeyDown={onKeyDown}
            >
                <div className="flex items-center gap-2 px-3.5 py-3 border-b border-line-subtle">
                    <Search className="w-5 h-5 text-ink-subtle shrink-0" aria-hidden="true" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                        placeholder="חפש לשונית, פעולה או מוצר…"
                        className="flex-1 bg-transparent outline-none text-base text-ink-strong placeholder:text-ink-faint"
                        aria-label="חיפוש"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="סגור"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 max-h-[60vh] overflow-y-auto p-2">
                    {filtered.length === 0 ? (
                        <div className="text-center py-10 text-sm text-ink-subtle">
                            לא נמצאו תוצאות עבור "{query}"
                        </div>
                    ) : (
                        grouped.map(([section, list]) => (
                            <div key={section} className="mb-1">
                                <div className="px-3 py-1.5 text-2xs font-bold tracking-wider text-ink-faint uppercase">
                                    {section}
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    {list.map((it) => {
                                        runningIndex += 1;
                                        const idx = runningIndex;
                                        return (
                                            <Item
                                                key={it.id}
                                                item={it}
                                                active={idx === active}
                                                onHover={() => setActive(idx)}
                                                onSelect={() => it.run()}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="px-3.5 py-2 border-t border-line-subtle bg-surface-muted/40 flex items-center justify-between text-2xs text-ink-subtle">
                    <div className="flex items-center gap-3">
                        <span><kbd className="px-1 py-0.5 rounded bg-surface border border-line text-ink-muted">↑</kbd>{' '}<kbd className="px-1 py-0.5 rounded bg-surface border border-line text-ink-muted">↓</kbd> ניווט</span>
                        <span><kbd className="px-1 py-0.5 rounded bg-surface border border-line text-ink-muted">↵</kbd> בחירה</span>
                        <span><kbd className="px-1 py-0.5 rounded bg-surface border border-line text-ink-muted">Esc</kbd> סגירה</span>
                    </div>
                    <div className="hidden sm:block">
                        Ctrl+K לפתיחה
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CommandPalette;
